import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BookOpen, RotateCcw, Search, Calendar as CalendarIcon, AlertTriangle, Check, Settings, DollarSign } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { format, addDays, differenceInCalendarDays } from "date-fns"; // Added differenceInCalendarDays for accurate calc
import { db } from "../../firebase/config";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  orderBy,
  where,
  increment,
  runTransaction,
  limit,
  startAfter,
  startAt,
  endAt,
  getDocs,
  getDoc,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  QueryDocumentSnapshot,
  DocumentData,
  setDoc,
} from "firebase/firestore";
import { FineConfig } from "../../../services/types";
import { getAuth, onAuthStateChanged } from "firebase/auth";

interface BorrowRecord {
  id: string;
  bookId: string;
  bookTitle: string;
  memberId: string;
  lookupId: string;
  memberName: string;
  borrowDate: string;
  dueDate: string;
  returnDate?: string;
  status: "borrowed" | "returned";
  fineAmount: number;
  renewCount: number;
  maxRenewals: number;
}

interface Book {
  id: string;
  title: string;
  author: string;
  isbn: string;
  category: string;
  shelf: string;
  quantity: number;
  available: number;
  status: "available" | "borrowed" | "reserved";
  coverImage?: string;
  description?: string;
  year?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface Member {
  id: string;
  name: string;
  memberId: string;
  role: "student" | "staff";
  borrowedBooks: number;
  maxBorrowLimit: number;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}



const BorrowReturnSystem = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [records, setRecords] = useState<BorrowRecord[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "borrowed" | "overdue" | "returned">("all");
  const [isBorrowDialogOpen, setIsBorrowDialogOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState("");
  const [bookSearchTerm, setBookSearchTerm] = useState("");
  const [selectedMember, setSelectedMember] = useState("");
  const [memberSearchTerm, setMemberSearchTerm] = useState("");

  const [dueDate, setDueDate] = useState<Date>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  
  // Server-side Pagination State
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageStack, setPageStack] = useState<QueryDocumentSnapshot<DocumentData>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  // Stats State
  const [stats, setStats] = useState({
    active: 0,
    overdue: 0,
    fines: 0,
    today: 0
  });

  const [isBorrowing, setIsBorrowing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // const [confirmRecordId, setConfirmRecordId] = useState(""); // Replaced by returnState
  const [confirmAction, setConfirmAction] = useState<'return' | 'renew'>('return');

  // Fine Config State
  const [fineConfig, setFineConfig] = useState<FineConfig>({
    currency: 'KES',
    // Late Return Fines
    finePerDay: 50,
    fineInterval: 'day',
    gracePeriodDays: 0,
    // Book Condition Fines
    lostBookFine: 2000,
    damagedBookFine: 500,
    processingFee: 100,
    // Fine Caps
    maxFinePerBook: 1000,
    maxFinePerUser: 5000,
    // Borrowing Policies
    maxBooksPerUser: 3,
    defaultLoanDays: 14,
    // Renewal Policies
    maxRenewals: 2,
    renewalExtensionDays: 14,
    allowRenewalWithFines: false,
    allowRenewalWhenOverdue: false,
    // Borrowing Restrictions
    blockBorrowingWithOverdue: true,
    blockBorrowingAtFineThreshold: 500,
  });
  const [isFineSettingsOpen, setIsFineSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Return/Renew Process State
  const [returnState, setReturnState] = useState({
    recordId: "",
    calculatedFine: 0,
    manualFine: 0,
    isManualOverride: false,
    condition: "good" as "good" | "lost" | "damaged",
    waived: false,
    remarks: ""
  });

  // Server-side Search State for Borrow Dialog
  const [bookSearchResults, setBookSearchResults] = useState<Book[]>([]);
  const [memberSearchResults, setMemberSearchResults] = useState<Member[]>([]);
  const [isSearchingBooks, setIsSearchingBooks] = useState(false);
  const [isSearchingMembers, setIsSearchingMembers] = useState(false);

  // Return Book Redesign States
  const [returnMemberSearchTerm, setReturnMemberSearchTerm] = useState("");
  const [returnMemberSearchResults, setReturnMemberSearchResults] = useState<Member[]>([]);
  const [isSearchingReturnMembers, setIsSearchingReturnMembers] = useState(false);
  const [returnSelectedMemberId, setReturnSelectedMemberId] = useState<string>("");
  const [memberBorrowedBooks, setMemberBorrowedBooks] = useState<BorrowRecord[]>([]);
  const [isLoadingMemberBooks, setIsLoadingMemberBooks] = useState(false);

  const bookSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const memberSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Server-side Book Search Function
  const searchBooksServer = useCallback(async (searchTerm: string) => {
    if (!schoolID || !searchTerm.trim()) {
      setBookSearchResults([]);
      return;
    }
    
    setIsSearchingBooks(true);
    try {
      const booksRef = collection(db, `schools/${schoolID}/books`);
      const term = searchTerm.toLowerCase();
      
      // Search by title prefix (Firestore limitation - can only do prefix search on one field)
      const titleQuery = query(
        booksRef,
        where("available", ">", 0),
        orderBy("available"),
        orderBy("title"),
        limit(20)
      );
      
      const snapshot = await getDocs(titleQuery);
      const results = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            author: data.author,
            isbn: data.isbn,
            category: data.category,
            shelf: data.shelf,
            quantity: data.quantity,
            available: data.available,
            status: data.status,
            coverImage: data.coverImage,
            description: data.description,
            year: data.year,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : undefined),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt ? new Date(data.updatedAt) : undefined),
          } as Book;
        })
        // Client-side filter for flexible matching (title, author, isbn)
        .filter((book) => 
          book.title?.toLowerCase().includes(term) ||
          book.author?.toLowerCase().includes(term) ||
          book.isbn?.includes(searchTerm)
        )
        .slice(0, 8);
      
      setBookSearchResults(results);
    } catch (error) {
      console.error("Error searching books:", error);
      setBookSearchResults([]);
    } finally {
      setIsSearchingBooks(false);
    }
  }, [schoolID]);

  // Server-side Member Search Function
  const searchMembersServer = useCallback(async (searchTerm: string) => {
    if (!schoolID || !searchTerm.trim()) {
      setMemberSearchResults([]);
      return;
    }
    
    setIsSearchingMembers(true);
    try {
      const term = searchTerm.toLowerCase();
      const results: Member[] = [];
      
      // Search students
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const studentsQuery = query(studentsRef, limit(50));
      const studentsSnapshot = await getDocs(studentsQuery);
      
      studentsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const name = `${data.firstName || ""} ${data.lastName || ""}`.trim().toLowerCase();
        const regNum = (data.registrationNumber || "").toLowerCase();
        
        if (name.includes(term) || regNum.includes(term)) {
          const borrowedBooks = data.borrowedBooks || 0;
          const maxLimit = 3;
          if (borrowedBooks < maxLimit) {
            results.push({
              id: doc.id,
              name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
              memberId: data.registrationNumber || "",
              role: "student" as const,
              borrowedBooks: borrowedBooks,
              maxBorrowLimit: maxLimit,
            });
          }
        }
      });
      
      // Search teachers
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teachersQuery = query(teachersRef, limit(50));
      const teachersSnapshot = await getDocs(teachersQuery);
      
      teachersSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const name = `${data.firstName || ""} ${data.lastName || ""}`.trim().toLowerCase();
        const empId = (data.employeeId || "").toLowerCase();
        
        if (name.includes(term) || empId.includes(term)) {
          const borrowedBooks = data.borrowedBooks || 0;
          const maxLimit = 5;
          if (borrowedBooks < maxLimit) {
            results.push({
              id: data.teacherId || doc.id,
              name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
              memberId: data.employeeId || "",
              role: "staff" as const,
              borrowedBooks: borrowedBooks,
              maxBorrowLimit: maxLimit,
            });
          }
        }
      });
      
      setMemberSearchResults(results.slice(0, 8));
    } catch (error) {
      console.error("Error searching members:", error);
      setMemberSearchResults([]);
    } finally {
      setIsSearchingMembers(false);
    }
  }, [schoolID]);

  // Debounced Book Search Effect
  useEffect(() => {
    if (bookSearchTimeoutRef.current) {
      clearTimeout(bookSearchTimeoutRef.current);
    }
    
    if (!bookSearchTerm.trim()) {
      setBookSearchResults([]);
      return;
    }
    
    bookSearchTimeoutRef.current = setTimeout(() => {
      searchBooksServer(bookSearchTerm);
    }, 300); // 300ms debounce
    
    return () => {
      if (bookSearchTimeoutRef.current) {
        clearTimeout(bookSearchTimeoutRef.current);
      }
    };
  }, [bookSearchTerm, searchBooksServer]);

  // Debounced Member Search Effect
  useEffect(() => {
    if (memberSearchTimeoutRef.current) {
      clearTimeout(memberSearchTimeoutRef.current);
    }
    
    if (!memberSearchTerm.trim()) {
      setMemberSearchResults([]);
      return;
    }
    
    memberSearchTimeoutRef.current = setTimeout(() => {
      searchMembersServer(memberSearchTerm);
    }, 300);
    
    return () => {
      if (memberSearchTimeoutRef.current) {
        clearTimeout(memberSearchTimeoutRef.current);
      }
    };
  }, [memberSearchTerm, searchMembersServer]);

  // Server-side Return Member Search Function (No max limit filtering)
  const searchReturnMembersServer = useCallback(async (searchTerm: string) => {
    if (!schoolID || !searchTerm.trim()) {
      setReturnMemberSearchResults([]);
      return;
    }
    
    setIsSearchingReturnMembers(true);
    try {
      const term = searchTerm.toLowerCase();
      const results: Member[] = [];
      
      // Search students
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const studentsQuery = query(studentsRef, limit(50)); 
      const studentsSnapshot = await getDocs(studentsQuery);
      
      studentsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const name = `${data.firstName || ""} ${data.lastName || ""}`.trim().toLowerCase();
        const regNum = (data.registrationNumber || "").toLowerCase();
        
        if (name.includes(term) || regNum.includes(term)) {
            results.push({
              id: doc.id,
              name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
              memberId: data.registrationNumber || "",
              role: "student" as const,
              borrowedBooks: data.borrowedBooks || 0,
              maxBorrowLimit: 3,
            });
        }
      });
      
      // Search teachers
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teachersQuery = query(teachersRef, limit(50));
      const teachersSnapshot = await getDocs(teachersQuery);
      
      teachersSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const name = `${data.firstName || ""} ${data.lastName || ""}`.trim().toLowerCase();
        const empId = (data.employeeId || "").toLowerCase();
        
        if (name.includes(term) || empId.includes(term)) {
            results.push({
              id: data.teacherId || doc.id,
              name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
              memberId: data.employeeId || "",
              role: "staff" as const,
              borrowedBooks: data.borrowedBooks || 0,
              maxBorrowLimit: 5,
            });
        }
      });
      
      setReturnMemberSearchResults(results.slice(0, 8));
    } catch (error) {
      console.error("Error searching return members:", error);
      setReturnMemberSearchResults([]);
    } finally {
      setIsSearchingReturnMembers(false);
    }
  }, [schoolID]);

  const fetchMemberBorrowedBooks = useCallback(async (member: Member) => {
      if (!schoolID || !member) return;
      setIsLoadingMemberBooks(true);
      try {
          const lookupValue = member.memberId || member.id;
          
          const recordsQuery = query(
              collection(db, `schools/${schoolID}/borrowRecords`),
              where("lookupId", "==", lookupValue),
              where("status", "==", "borrowed")
          );
          const snapshot = await getDocs(recordsQuery);
          const books = snapshot.docs.map(doc => {
            const data = doc.data();
            // Helper to safe convert timestamp/date to string
            const safeDate = (val: any) => {
                if (!val) return "";
                if (val.toDate) return format(val.toDate(), "yyyy-MM-dd");
                if (val instanceof Date) return format(val, "yyyy-MM-dd");
                return String(val); // fallback for existing strings
            };

            return {
                id: doc.id,
                ...data,
                borrowDate: safeDate(data.borrowDate),
                dueDate: safeDate(data.dueDate),
                returnDate: data.returnDate ? safeDate(data.returnDate) : undefined,
            } as BorrowRecord;
          });
          setMemberBorrowedBooks(books);
      } catch (error) {
          console.error("Error fetching borrowed books:", error);
      } finally {
          setIsLoadingMemberBooks(false);
      }
  }, [schoolID]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
        if (returnMemberSearchTerm.trim()) {
            searchReturnMembersServer(returnMemberSearchTerm);
        } else {
            setReturnMemberSearchResults([]);
        }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [returnMemberSearchTerm, searchReturnMembersServer]);

  const findMemberRef = useCallback(async (lookupId: string, schoolId: string) => {
    if (!lookupId) {
      return null;
    }

    let q = query(
      collection(db, `schools/${schoolId}/students`),
      where("registrationNumber", "==", lookupId),
      limit(1)
    );
    let snaps = await getDocs(q);
    if (!snaps.empty) {
      return {
        memberRef: snaps.docs[0].ref,
        memberData: snaps.docs[0].data(),
      };
    }

    q = query(
      collection(db, `schools/${schoolId}/teachers`),
      where("employeeId", "==", lookupId),
      limit(1)
    );
    snaps = await getDocs(q);
    if (!snaps.empty) {
      return {
        memberRef: snaps.docs[0].ref,
        memberData: snaps.docs[0].data(),
      };
    }

    const sRef = doc(db, `schools/${schoolId}/students`, lookupId);
    let snap = await getDoc(sRef);
    if (snap.exists()) {
      return {
        memberRef: sRef,
        memberData: snap.data(),
      };
    }

    const tRef = doc(db, `schools/${schoolId}/teachers`, lookupId);
    snap = await getDoc(tRef);
    if (snap.exists()) {
      return {
        memberRef: tRef,
        memberData: snap.data(),
      };
    }

    return null;
  }, []);

  // Fetch Stats
  const fetchStats = async (sID: string) => {
    const recordsRef = collection(db, `schools/${sID}/borrowRecords`);
    
    try {
      // 1. Total records (Critical for pagination)
      try {
        const totalSnap = await getCountFromServer(recordsRef);
        setTotalRecords(totalSnap.data().count);
      } catch (e) {
        console.error("Error fetching total count:", e);
      }

      // 2. Active Count & Overdue Count & Pending Fines
      let activeCount = 0;
      let overdueCount = 0;
      let pendingFines = 0;
      
      try {
          const activeQuery = query(recordsRef, where("status", "==", "borrowed"));
          const activeDocsSnap = await getDocs(activeQuery);
          activeCount = activeDocsSnap.size;
          
          const now = new Date();
          // Filter overdue client-side and calc pending fines
          overdueCount = activeDocsSnap.docs.filter(doc => {
              const data = doc.data();
              if (!data.dueDate) return false;
              const due = data.dueDate.toDate ? data.dueDate.toDate() : new Date(data.dueDate);
              
              if (due < now) {
                  const diff = Math.max(0, differenceInCalendarDays(now, due));
                  let fine = diff * fineConfig.finePerDay;
                  if (fineConfig.fineInterval === 'week') {
                      fine = Math.ceil(diff / 7) * fineConfig.finePerDay;
                  }
                  pendingFines += fine;
                  return true;
              }
              return false;
          }).length;
      } catch (e) {
          console.error("Error fetching active/overdue stats:", e);
      }

      // 3. Fines (Total Collected + Pending)
      let totalFines = 0;
      try {
          const finesSnap = await getAggregateFromServer(recordsRef, {
            totalFines: sum('fineAmount')
          });
          const collectedFines = finesSnap.data().totalFines || 0;
          totalFines = collectedFines + pendingFines;
      } catch (e) {
          console.error("Error fetching fines stats:", e);
      }

      // 4. Returned Today
      let todayCount = 0;
      try {
          const todayStart = new Date();
          todayStart.setHours(0,0,0,0);
          const todayEnd = new Date();
          todayEnd.setHours(23,59,59,999);
          const todaySnap = await getCountFromServer(query(
              recordsRef, 
              where("returnDate", ">=", todayStart), 
              where("returnDate", "<=", todayEnd)
          ));
          todayCount = todaySnap.data().count;
      } catch (e) {
          console.error("Error fetching today stats:", e);
      }

      setStats({
        active: activeCount,
        overdue: overdueCount,
        fines: totalFines,
        today: todayCount
      });
      
    } catch (err) {
      console.error("Fetch stats error:", err);
    }
  };

  // Fetch Fine Config
  const fetchFineConfig = async (sID: string) => {
    try {
      const configRef = doc(db, `schools/${sID}/librarySettings/fines`);
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        const data = configSnap.data() as FineConfig;
        setFineConfig(data);
      } else {
        // Init default if not exists
        const defaultConfig: FineConfig = {
          currency: 'KES',
          finePerDay: 50,
          fineInterval: 'day',
          gracePeriodDays: 0,
          lostBookFine: 2000,
          damagedBookFine: 500,
          processingFee: 100,
          maxFinePerBook: 1000,
          maxFinePerUser: 5000,
          maxBooksPerUser: 3,
          defaultLoanDays: 14,
          maxRenewals: 2,
          renewalExtensionDays: 14,
          allowRenewalWithFines: false,
          allowRenewalWhenOverdue: false,
          blockBorrowingWithOverdue: true,
          blockBorrowingAtFineThreshold: 500,
        };
        setFineConfig(defaultConfig);
        try {
           await setDoc(configRef, defaultConfig);
        } catch (setErr) {
           // Ignore set permission errors, just use local default
        }
      }
    } catch (err: any) {
      // Permission errors are common if settings don't exist/rules are strict. 
      // Fallback to default without noise.
      if (err.code !== 'permission-denied') {
         console.warn("Error fetching fine config:", err);
      }
    }
  };

  // Main Effect for Auth & Subscriptions
  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;

    const auth = getAuth();
    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        navigate("/login-page");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        let selectedAffiliation = defaultAffiliation && (defaultAffiliation.role === "admin" || defaultAffiliation.role === "school_staff") && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;
        
        const selectedSchoolID = selectedAffiliation?.schoolID || claimSchoolID || null;
        setSchoolID(selectedSchoolID);

        if (selectedSchoolID) {
          // fetch stats
          fetchStats(selectedSchoolID);
          fetchFineConfig(selectedSchoolID);

          // Note: Books and Members are now fetched on-demand via server-side search
          // in the Borrow dialog to improve performance for large datasets.
          
          setLoading(false);
        } else {
          throw new Error("No school affiliation found.");
        }
      } catch (error: any) {
        console.error("Error verifying user claims:", error);
        setLoading(false);
        setError("Failed to verify permissions.");
      }
    });

    return () => {
      unsubscribeAuth?.();
    };
  }, [navigate]);

  // Effect for Fetching Records with Pagination
  useEffect(() => {
    if (!schoolID) return;
    
    let unsubscribe: () => void;
    
    setLoading(true);
    const recordsRef = collection(db, `schools/${schoolID}/borrowRecords`);
    let q;

    // Search Mode (Title Prefix)
    // BorrowReturnSystem traditionally searches bookTitle, memberName, memberId.
    // Server-side strict search usually only supports one field with sort.
    // We will prioritize bookTitle for server search to match BooksManagement pattern.
    if (searchTerm.trim()) {
      q = query(
          recordsRef, 
          orderBy("bookTitle"), 
          startAt(searchTerm), 
          endAt(searchTerm + '\uf8ff'),
          limit(itemsPerPage)
      );
      
      // Handle pagination for search
       if (currentPage > 1 && pageStack.length >= currentPage - 1) {
          const cursor = pageStack[currentPage - 2];
           q = query(
              recordsRef, 
              orderBy("bookTitle"), 
              startAt(searchTerm), 
              endAt(searchTerm + '\uf8ff'),
              startAfter(cursor),
              limit(itemsPerPage)
          );
      }
    } else {
      // Default Mode
      q = query(recordsRef, orderBy("borrowDate", "desc"), limit(itemsPerPage));
      
      if (currentPage > 1 && pageStack.length >= currentPage - 1) {
          const cursor = pageStack[currentPage - 2];
          q = query(recordsRef, orderBy("borrowDate", "desc"), startAfter(cursor), limit(itemsPerPage));
      }
    }

    unsubscribe = onSnapshot(q, (snapshot) => {
        const recordData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            bookId: data.bookId,
            bookTitle: data.bookTitle,
            memberId: data.memberId,
            lookupId: data.lookupId || data.memberId,
            memberName: data.memberName,
            borrowDate: data.borrowDate?.toDate ? data.borrowDate.toDate().toISOString().split("T")[0] : data.borrowDate,
            dueDate: data.dueDate?.toDate ? data.dueDate.toDate().toISOString().split("T")[0] : data.dueDate,
            returnDate: data.returnDate ? (data.returnDate.toDate ? data.returnDate.toDate().toISOString().split("T")[0] : data.returnDate) : undefined,
            status: data.status,
            fineAmount: data.fineAmount || 0,
            renewCount: data.renewCount || 0,
            maxRenewals: data.maxRenewals || 2,
          } as BorrowRecord;
        });
        
        setRecords(recordData);
        if (snapshot.docs.length > 0) {
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        }
        setLoading(false);
    }, (err) => {
      console.error("Fetch records error:", err);
      // Don't set error state globally to avoid navigation, just log.
      setLoading(false);
    });
    
    return () => {
        if (unsubscribe) unsubscribe();
    };
  }, [schoolID, searchTerm, currentPage]);

  const handleNextPage = () => {
      if (records.length < itemsPerPage) return;
      if (lastVisible) {
          setPageStack(prev => [...prev, lastVisible]);
          setCurrentPage(prev => prev + 1);
      }
  };

  const handlePrevPage = () => {
      if (currentPage > 1) {
          setPageStack(prev => prev.slice(0, -1));
          setCurrentPage(prev => prev - 1);
      }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setPageStack([]);
  };

  const getEffectiveStatus = (record: BorrowRecord): "borrowed" | "overdue" | "returned" => {
    if (record.status === "returned") return "returned";
    const today = new Date().toISOString().split("T")[0];
    return record.dueDate < today ? "overdue" : "borrowed";
  };

  const filteredRecords = useMemo(() => {
    // We only client-side filter the CURRENT PAGE by status
    return records.filter((record) => {
      const effectiveStatus = getEffectiveStatus(record);
      const matchesStatus = filterStatus === "all" || effectiveStatus === filterStatus;
      return matchesStatus;
    });
  }, [records, filterStatus]);

  // Estimated pages
  const estimatedTotalPages = Math.ceil(totalRecords / itemsPerPage) || 1;

  const getDisplayReg = useCallback((record: BorrowRecord) => {
    const isUid = record.memberId && (record.memberId.startsWith('student_') || record.memberId.startsWith('teacher_'));
    return isUid ? (members.find(m => m.id === record.memberId)?.memberId || 'N/A') : (record.memberId || 'N/A');
  }, [members]);

  const handleBorrowBook = async () => {
    if (isBorrowing) return;
    if (!selectedBook || !selectedMember || !dueDate || !schoolID) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    const book = books.find((b) => b.id === selectedBook);
    const member = members.find((m) => m.id === selectedMember);

    if (!book || book.available === 0) {
      toast({
        title: "Error",
        description: "Book is not available",
        variant: "destructive",
      });
      return;
    }

    if (!member || member.borrowedBooks >= member.maxBorrowLimit) {
      toast({
        title: "Error",
        description: "Member has reached borrowing limit",
        variant: "destructive",
      });
      return;
    }

    const lookupId = member.memberId || member.id;

    try {
      const existingQuery = query(
        collection(db, `schools/${schoolID}/borrowRecords`),
        where("bookId", "==", selectedBook),
        where("lookupId", "==", lookupId),
        where("status", "==", "borrowed")
      );
      const existingSnaps = await getDocs(existingQuery);
      if (!existingSnaps.empty) {
        throw new Error("You already have this book borrowed and not returned.");
      }

      setIsBorrowing(true);
      await runTransaction(db, async (transaction) => {
        const bookRef = doc(db, `schools/${schoolID}/books`, selectedBook);
        const bookSnap = await transaction.get(bookRef);
        if (!bookSnap.exists()) {
          throw new Error("Book not found");
        }
        const bookData = bookSnap.data();
        if (bookData.available <= 0) {
          throw new Error("Book is not available");
        }

        const basePath = member.role === "student" ? "students" : "teachers";
        const memberRef = doc(db, `schools/${schoolID}/${basePath}`, member.id);
        const memberSnap = await transaction.get(memberRef);
        if (!memberSnap.exists()) {
          throw new Error("Member not found");
        }
        const memberData = memberSnap.data();
        if (memberData.borrowedBooks >= (fineConfig.maxBooksPerUser || memberData.maxBorrowLimit)) {
           // We prioritize the specialized member limit if it's higher? Or Global limit?
           // Usually specific overrides global. 
           // But user asked to set "someone cant borrow if they have X books".
           // Let's check against the global config if it exists, but usually members have their own logic.
           // Let's behave as: Global is the default ceiling, unless member has a specific override?
           // For simplicity: If member.maxBorrowLimit is set and different from default, use it. 
           // If we just want to ENFORCE the global limit:
           // throw new Error(`Borrow limit reached (Max: ${fineConfig.maxBooksPerUser})`);
           // But let's stick to the existing logic but alert if it exceeds global.
        }
        // Actually, let's enforce the stricter of the two or just the member's limit which we should sync with global default on creation.
        // For now, let's just create the check:
        const limit = fineConfig.maxBooksPerUser || memberData.maxBorrowLimit || 2;
        if (memberData.borrowedBooks >= limit) {
           throw new Error(`Member has reached borrowing limit of ${limit} books.`);
        }

        const borrowDate = new Date();
        const borrowRecordsRef = collection(db, `schools/${schoolID}/borrowRecords`);
        const newRecordRef = doc(borrowRecordsRef);
        const displayId = member.memberId;
        transaction.set(newRecordRef, {
          bookId: selectedBook,
          bookTitle: book.title,
          memberId: displayId,
          lookupId,
          memberName: member.name,
          borrowDate,
          dueDate: dueDate,
          status: "borrowed",
          fineAmount: 0,
          renewCount: 0,
          maxRenewals: 2,
        });

        transaction.update(memberRef, { borrowedBooks: increment(1) });
        transaction.update(bookRef, { available: increment(-1) });
      });

      if (!members.find((m) => m.id === selectedMember)?.memberId) {
        toast({
          title: "Warning",
          description: "Member ID missing—using N/A for display and doc ID for lookup. Update profile for proper reg/employee number.",
          variant: "default",
          duration: 5000,
        });
      }

      setIsBorrowDialogOpen(false);
      setSelectedBook("");
      setBookSearchTerm("");
      setSelectedMember("");
      setMemberSearchTerm("");
      setDueDate(undefined);

      toast({
        title: "Success",
        description: "Book borrowed successfully",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error borrowing book:", error);
      toast({
        title: "Error",
        description: `Failed to borrow book: ${error.message}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsBorrowing(false);
    }
  };

  const handleSaveFineSettings = async () => {
      if (!schoolID) return;
      setSavingSettings(true);
      try {
        await setDoc(doc(db, `schools/${schoolID}/librarySettings/fines`), fineConfig);
        fetchStats(schoolID);
        toast({ title: "Settings Saved", description: "Fine configuration updated successfully." });
        setIsFineSettingsOpen(false);
      } catch (e: any) {
        toast({ title: "Error", description: e.message, variant: "destructive" });
      } finally {
        setSavingSettings(false);
      }
  };

  const calculateDynamicFine = (record: BorrowRecord) => {
    if (!record) return 0;
    const today = new Date();
    const dueDate = new Date(record.dueDate);
    // differenceInCalendarDays returns (dateLeft - dateRight)
    const overdueDays = Math.max(0, differenceInCalendarDays(today, dueDate));
    
    if (overdueDays <= 0) return 0;

    let fine = overdueDays * fineConfig.finePerDay; // Assuming per day for now
    if (fineConfig.fineInterval === 'week') {
        fine = Math.ceil(overdueDays / 7) * fineConfig.finePerDay;
    }
    
    // NOTE: Max fine per user usually applies to the sum of all their debts. 
    // Here we can cap the *transaction* fine if desired, but typically simple libraries cap per book or per user total.
    // We will just return the raw calculation here and let the confirm dialog show it.
    return fine;
  };



  const handleReturnBook = (recordId: string) => {
    initiateReturn(recordId);
  };

  const initiateReturn = (recordId: string) => {
    const record = records.find(r => r.id === recordId);
    if (!record) return;

    const fine = calculateDynamicFine(record);
    setReturnState({
      recordId,
      calculatedFine: fine,
      manualFine: fine,
      isManualOverride: false,
      condition: "good",
      waived: false,
      remarks: ""
    });
    setConfirmAction('return');
    setIsConfirmOpen(true);
  };

  const handleRenewBook = (recordId: string) => {
    // Renew Logic remains similar but uses returnState for ID tracking
    setReturnState(prev => ({ ...prev, recordId }));
    setConfirmAction('renew');
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    const recordId = returnState.recordId;
    const record = records.find((r) => r.id === recordId);
    if (!record || !schoolID) {
      setIsConfirmOpen(false);
      return;
    }

    setIsConfirmOpen(false);

    if (confirmAction === 'return') {
      try {
        const memberInfo = await findMemberRef(record.lookupId, schoolID);
        if (!memberInfo) {
            // Allow force return even if member ref issue? Ideally yes but for now throw.
            throw new Error("Linked member record not found.");
        }
        const { memberRef } = memberInfo;

        let finalFine = returnState.waived ? 0 : (returnState.isManualOverride ? returnState.manualFine : returnState.calculatedFine);
        
        // Add lost/damaged costs automatically if applicable and not manually overridden
        // Wait, if user selects 'Lost', we usually REPLACE the overdue fine with Lost Book Fine, or ADD it?
        // Standard practice: Lost Book Fine + Processing Fee (maybe overdue fine is waived or capped).
        // Let's implement: If Lost/Damaged, use that config price instead of overdue fine, 
        // UNLESS the user manually edited the fine field.
        // Actually, the UI should handle the "suggestion" of fine amount when condition changes.
        // So here we just trust `finalFine` from state which the user saw.
        
        await runTransaction(db, async (transaction) => {
          const borrowRef = doc(db, `schools/${schoolID}/borrowRecords`, recordId);
          const borrowSnap = await transaction.get(borrowRef);
          if (!borrowSnap.exists() || borrowSnap.data()?.status !== "borrowed") {
            throw new Error("Record not available.");
          }

          const bookRef = doc(db, `schools/${schoolID}/books`, record.bookId);
          
          // If returning, we decrement member borrowed count.
          // If lost, we might NOT increment book available count (it's gone).
          // If damaged, we might increment but mark as need repair? 
          // For simplicity: Lost -> Book Quantity -1 (removed from stock). Damaged -> Book Available +1 but maybe status update?
          // Simplification: 
          // Good: Available +1
          // Damaged: Available +1 (assuming repairable)
          // Lost: Quantity -1, Available unchanged (since it was 0 while borrowed).
          
          if (returnState.condition === 'lost') {
             transaction.update(bookRef, { quantity: increment(-1) });
          } else {
             // Good or Damaged (returned to shelf)
             transaction.update(bookRef, { available: increment(1) });
          }

          transaction.update(borrowRef, {
            returnDate: new Date(),
            status: "returned",
            fineAmount: finalFine,
            condition: returnState.condition,
            remarks: returnState.remarks
          });

          transaction.update(memberRef, {
            borrowedBooks: increment(-1),
            // We could update a 'totalFinesAccrued' field on user here if tracking totals
          });
        });

        toast({
          title: "Book Returned",
          description: `Processed as ${returnState.condition}. Fine: ${fineConfig.currency} ${finalFine}`,
        });

      } catch (error: any) {
        console.error("Error returning book:", error);
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    } else if (confirmAction === 'renew') {
      // Existing renew logic
      if (record.renewCount >= record.maxRenewals) {
        toast({ title: "Error", description: "Max renewals reached", variant: "destructive" });
        return;
      }
      if (getEffectiveStatus(record) === "overdue") {
        toast({ title: "Error", description: "Cannot renew overdue books", variant: "destructive" });
        return;
      }

      try {
         const newDueDate = addDays(new Date(record.dueDate), 14); // 2 weeks default
         await updateDoc(doc(db, `schools/${schoolID}/borrowRecords`, recordId), {
            dueDate: newDueDate,
            renewCount: increment(1)
         });
         toast({ title: "Success", description: "Renewed for 14 days." });
      } catch (e: any) {
         toast({ title: "Error", description: e.message });
      }
    }
  };

  const getStatusBadge = (record: BorrowRecord) => {
    const effectiveStatus = getEffectiveStatus(record);
    let badgeClass = "";
    let label = "";
    let icon = null;

    switch (effectiveStatus) {
      case "returned":
        badgeClass = "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
        label = "Returned";
        break;
      case "overdue":
        badgeClass = "bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white";
        label = "Overdue";
        icon = <AlertTriangle className="h-4 w-4" />;
        break;
      case "borrowed":
        badgeClass = "bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white";
        label = "Borrowed";
        break;
    }

    return (
      <Badge className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate gap-1 ${badgeClass}`}>
        {icon}
        {label}
      </Badge>
    );
  };



  const confirmRecord = records.find((r) => r.id === returnState.recordId);

  if (loading) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <p className="text-foreground text-center">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 px-4 rounded-full flex items-center gap-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Helper for return dialog fine display
  const currentTotalFine = returnState.waived ? 0 : (returnState.isManualOverride ? returnState.manualFine : returnState.calculatedFine);
  
  const handleConditionChange = (val: "good" | "lost" | "damaged") => {
     let recommendedFine = returnState.calculatedFine; // defaulting to overdue amount
     if (val === 'lost') recommendedFine = fineConfig.lostBookFine;
     if (val === 'damaged') recommendedFine = fineConfig.damagedBookFine;
     
     // If user hasn't overridden manually yet, auto-update the manualFine/calculated display
     // Actually, let's update manualFine to this recommended amount and set override to true automatically?
     // Or just update manualFine and use it if override is checked?
     // Better UX: Update the "calculated" base or pre-fill the manual override input.
     
     setReturnState(prev => ({
        ...prev,
        condition: val,
        manualFine: val === 'good' ? prev.calculatedFine : recommendedFine,
        isManualOverride: val !== 'good', // Auto-enable override for lost/damaged
     }));
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto {
            font-family: 'Roboto', sans-serif;
          }
          @media (max-width: 767px) {
            /* prevent horizontal page scroll */
            body { overflow-x: hidden; }

            /* make the table container span the full viewport but keep the parent's horizontal padding
               so the first column header doesn't get clipped on small screens */
            .mobile-table-container {
              width: 100vw;
              margin-left: calc(-50vw + 50%);
              padding-left: 16px;
              padding-right: 16px;
              box-sizing: border-box;
            }

            /* ensure the table header's first cell aligns with the body cells */
            .mobile-table-container table thead th:first-child {
              padding-left: 16px;
            }

            /* keep existing per-cell padding for the book column */
            .book-cell {
              padding-left: 16px !important;
              padding-right: 16px !important;
            }
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 flex-shrink-0">
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-2xl font-bold text-black dark:text-white">
                  {stats.active}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Overdue</CardTitle>
                <AlertTriangle className="h-4 w-4 text-[#EF4444] dark:text-[#F87171]" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-2xl font-bold text-[#EF4444] dark:text-[#F87171]">
                  {stats.overdue}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Fines</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-2xl font-bold text-black dark:text-white">
                  {fineConfig.currency} {stats.fines.toFixed(0)}
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Today</CardTitle>
                <RotateCcw className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-2xl font-bold text-black dark:text-white">
                  {stats.today}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fine Configuration Section - Collapsible */}
           {isFineSettingsOpen && (
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader className="pb-3 border-b border-[#F1F5F9] dark:border-[#1E293B]">
                 <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="text-base font-semibold">Library Policy Configuration</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">Configure fines, borrowing limits, and renewal policies.</p>
                    </div>
                    <Button onClick={handleSaveFineSettings} disabled={savingSettings} size="sm" className="bg-[#2563EB] text-white hover:bg-[#1E40AF]">
                        {savingSettings ? "Saving..." : "Save Changes"}
                    </Button>
                 </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-8">
                 {/* Section 1: Late Return Fines */}
                 <div>
                    <h3 className="text-sm font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                       <AlertTriangle className="h-4 w-4 text-amber-500" /> Late Return Fines
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Currency</Label>
                          <Input value={fineConfig.currency} onChange={e => setFineConfig({...fineConfig, currency: e.target.value})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Fine Per Day</Label>
                          <Input type="number" value={fineConfig.finePerDay} onChange={e => setFineConfig({...fineConfig, finePerDay: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Fine Interval</Label>
                          <Select value={fineConfig.fineInterval} onValueChange={(val: 'day' | 'week') => setFineConfig({...fineConfig, fineInterval: val})}>
                             <SelectTrigger className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]"><SelectValue /></SelectTrigger>
                             <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                               <SelectItem value="day" className="text-black dark:text-white">Per Day</SelectItem>
                               <SelectItem value="week" className="text-black dark:text-white">Per Week</SelectItem>
                             </SelectContent>
                          </Select>
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Grace Period (Days)</Label>
                          <Input type="number" value={fineConfig.gracePeriodDays} onChange={e => setFineConfig({...fineConfig, gracePeriodDays: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                    </div>
                 </div>

                 {/* Section 2: Book Condition Fines */}
                 <div>
                    <h3 className="text-sm font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                       <BookOpen className="h-4 w-4 text-red-500" /> Book Condition Fines
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Lost Book Fee</Label>
                          <Input type="number" value={fineConfig.lostBookFine} onChange={e => setFineConfig({...fineConfig, lostBookFine: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Damaged Book Fee</Label>
                          <Input type="number" value={fineConfig.damagedBookFine} onChange={e => setFineConfig({...fineConfig, damagedBookFine: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Processing Fee</Label>
                          <Input type="number" value={fineConfig.processingFee} onChange={e => setFineConfig({...fineConfig, processingFee: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                    </div>
                 </div>

                 {/* Section 3: Fine Caps */}
                 <div>
                    <h3 className="text-sm font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                       <DollarSign className="h-4 w-4 text-green-500" /> Fine Caps
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Max Fine Per Book</Label>
                          <Input type="number" value={fineConfig.maxFinePerBook} onChange={e => setFineConfig({...fineConfig, maxFinePerBook: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Max Fine Per User</Label>
                          <Input type="number" value={fineConfig.maxFinePerUser} onChange={e => setFineConfig({...fineConfig, maxFinePerUser: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                    </div>
                 </div>

                 {/* Section 4: Borrowing Policies */}
                 <div>
                    <h3 className="text-sm font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                       <BookOpen className="h-4 w-4 text-blue-500" /> Borrowing Policies
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Max Books Per User</Label>
                          <Input type="number" value={fineConfig.maxBooksPerUser} onChange={e => setFineConfig({...fineConfig, maxBooksPerUser: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Default Loan Period (Days)</Label>
                          <Input type="number" value={fineConfig.defaultLoanDays} onChange={e => setFineConfig({...fineConfig, defaultLoanDays: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                    </div>
                 </div>

                 {/* Section 5: Renewal Policies */}
                 <div>
                    <h3 className="text-sm font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                       <RotateCcw className="h-4 w-4 text-purple-500" /> Renewal Policies
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Max Renewals Per Book</Label>
                          <Input type="number" value={fineConfig.maxRenewals} onChange={e => setFineConfig({...fineConfig, maxRenewals: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Renewal Extension (Days)</Label>
                          <Input type="number" value={fineConfig.renewalExtensionDays} onChange={e => setFineConfig({...fineConfig, renewalExtensionDays: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                       <div className="flex items-center gap-3 p-3 rounded-md bg-gray-50 dark:bg-gray-900 border border-[#E2E8F0] dark:border-[#1E293B]">
                          <input type="checkbox" id="renewWithFines" className="h-4 w-4" checked={fineConfig.allowRenewalWithFines} onChange={e => setFineConfig({...fineConfig, allowRenewalWithFines: e.target.checked})} />
                          <Label htmlFor="renewWithFines" className="text-xs cursor-pointer">Allow Renewal With Fines</Label>
                       </div>
                       <div className="flex items-center gap-3 p-3 rounded-md bg-gray-50 dark:bg-gray-900 border border-[#E2E8F0] dark:border-[#1E293B]">
                          <input type="checkbox" id="renewOverdue" className="h-4 w-4" checked={fineConfig.allowRenewalWhenOverdue} onChange={e => setFineConfig({...fineConfig, allowRenewalWhenOverdue: e.target.checked})} />
                          <Label htmlFor="renewOverdue" className="text-xs cursor-pointer">Allow Renewal When Overdue</Label>
                       </div>
                    </div>
                 </div>

                 {/* Section 6: Borrowing Restrictions */}
                 <div>
                    <h3 className="text-sm font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                       <AlertTriangle className="h-4 w-4 text-red-500" /> Borrowing Restrictions
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                       <div className="flex items-center gap-3 p-3 rounded-md bg-gray-50 dark:bg-gray-900 border border-[#E2E8F0] dark:border-[#1E293B]">
                          <input type="checkbox" id="blockOverdue" className="h-4 w-4" checked={fineConfig.blockBorrowingWithOverdue} onChange={e => setFineConfig({...fineConfig, blockBorrowingWithOverdue: e.target.checked})} />
                          <Label htmlFor="blockOverdue" className="text-xs cursor-pointer">Block Borrowing If User Has Overdue Books</Label>
                       </div>
                       <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Block Borrowing At Fine Threshold (0=disabled)</Label>
                          <Input type="number" value={fineConfig.blockBorrowingAtFineThreshold} onChange={e => setFineConfig({...fineConfig, blockBorrowingAtFineThreshold: Number(e.target.value)})} className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]" />
                       </div>
                    </div>
                 </div>
              </CardContent>
            </Card>
           )}

          <div className="flex flex-col gap-3 sm:gap-4 flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 w-full bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6] h-10"
                />
              </div>
              <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value as any)}>
                <SelectTrigger className="w-full sm:w-40 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                  <SelectItem value="all" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">All Status</SelectItem>
                  <SelectItem value="borrowed" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Borrowed</SelectItem>
                  <SelectItem value="overdue" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Overdue</SelectItem>
                  <SelectItem value="returned" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Returned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setIsFineSettingsOpen(!isFineSettingsOpen)}
                  className="m-2 flex-shrink-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
              >
                  <Settings className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">{isFineSettingsOpen ? "Hide Settings" : "Fine Settings"}</span>
              </Button>

              <Dialog open={isBorrowDialogOpen} onOpenChange={setIsBorrowDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="m-2 flex-shrink-0 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] h-10">
                    <BookOpen className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Borrow Book</span>
                    <span className="sm:hidden">Borrow</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] p-0">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-[#F1F5F9] dark:border-[#1E293B] bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-bold text-black dark:text-white flex items-center gap-2">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                          <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        Issue New Book
                      </DialogTitle>
                      <DialogDescription className="text-sm text-muted-foreground mt-1">
                        Complete the form below to issue a book to a library member.
                      </DialogDescription>
                    </DialogHeader>
                  </div>

                  {/* Form Content */}
                  <div className="px-6 py-5 space-y-6">
                    {/* Book Selection Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 text-xs font-bold">1</div>
                        <Label className="text-sm font-semibold text-black dark:text-white">Search Book</Label>
                      </div>
                      
                      {/* Search Input with Real-time Suggestions */}
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Type title, author or ISBN..."
                            value={selectedBook ? books.find(b => b.id === selectedBook)?.title || '' : bookSearchTerm}
                            onChange={(e) => {
                              setBookSearchTerm(e.target.value);
                              if (selectedBook) setSelectedBook("");
                            }}
                            onFocus={() => {
                              if (selectedBook) {
                                setBookSearchTerm("");
                                setSelectedBook("");
                              }
                            }}
                            className="pl-10 pr-10 h-11 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] focus:border-blue-500 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/50"
                          />
                          {(bookSearchTerm || selectedBook) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
                              onClick={() => {
                                setBookSearchTerm("");
                                setSelectedBook("");
                              }}
                            >
                              <span className="text-gray-400 text-lg">×</span>
                            </Button>
                          )}
                        </div>
                        
                        {/* Real-time Suggestions Dropdown */}
                        {bookSearchTerm && !selectedBook && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-lg overflow-hidden">
                            {isSearchingBooks ? (
                              <div className="p-4 text-center text-sm text-muted-foreground">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                                  Searching...
                                </div>
                              </div>
                            ) : bookSearchResults.length === 0 ? (
                              <div className="p-4 text-center text-sm text-muted-foreground">
                                <p>No books found matching "{bookSearchTerm}"</p>
                              </div>
                            ) : (
                              <div className="max-h-64 overflow-y-auto">
                                {bookSearchResults.map((book) => {
                                  const year = book.year || 'N/A';
                                  // Simple category styling map
                                  const categoryStyle = {
                                    'Fiction': 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 border-purple-100 dark:border-purple-800',
                                    'Non-Fiction': 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border-blue-100 dark:border-blue-800',
                                    'Science': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border-emerald-100 dark:border-emerald-800',
                                    'Technology': 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300 border-cyan-100 dark:border-cyan-800',
                                    'History': 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border-amber-100 dark:border-amber-800',
                                  }[book.category || ''] || 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-100 dark:border-gray-800';

                                  return (
                                    <button
                                      key={book.id}
                                      type="button"
                                      className="w-full px-4 py-3 text-left hover:bg-blue-50/50 dark:hover:bg-blue-900/10 border-b border-gray-100 dark:border-gray-800 last:border-0 transition-colors group"
                                      onClick={() => {
                                        setSelectedBook(book.id);
                                        setBookSearchTerm("");
                                        setBooks(prev => {
                                          if (prev.find(b => b.id === book.id)) return prev;
                                          return [...prev, book];
                                        });
                                      }}
                                    >
                                      <div className="flex gap-4">
                                        <div className="w-12 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-md shadow-sm overflow-hidden flex-shrink-0 border border-gray-200 dark:border-gray-700 group-hover:border-blue-200 dark:group-hover:border-blue-800 transition-colors">
                                          {book.coverImage ? (
                                            <img src={book.coverImage} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                              <BookOpen className="h-5 w-5 text-gray-400" />
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                                          <div>
                                            <div className="flex justify-between items-start gap-2">
                                              <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                {book.title}
                                              </span>
                                              <span className="text-[10px] items-center px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 whitespace-nowrap hidden sm:inline-flex">
                                                {year}
                                              </span>
                                            </div>
                                            <div className="text-xs text-muted-foreground line-clamp-1">{book.author}</div>
                                          </div>
                                          
                                          <div className="flex items-center gap-2 mt-1.5">
                                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 border ${categoryStyle}`}>
                                              {book.category || 'General'}
                                            </Badge>
                                            <div className="flex-1" />
                                            {book.available > 0 ? (
                                              <span className="text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded-full">
                                                {book.available} avail
                                              </span>
                                            ) : (
                                              <span className="text-[10px] font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded-full">
                                                Out of stock
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Book Preview Card */}
                      {selectedBook && (() => {
                        const book = books.find(b => b.id === selectedBook);
                        if (!book) return null;
                        return (
                          <div className="mt-3 p-5 rounded-xl bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-100 dark:border-blue-900/40 relative overflow-hidden group">
                           {/* Decorative background element */}
                           <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/20 to-purple-100/20 dark:from-blue-500/5 dark:to-purple-500/5 rounded-bl-full -mr-10 -mt-10 pointer-events-none" />
                           
                            <div className="flex gap-5 relative z-10">
                              <div className="w-24 h-36 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex-shrink-0">
                                {book.coverImage ? (
                                  <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                                    <BookOpen className="h-10 w-10 text-blue-200 dark:text-blue-800" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0 flex flex-col">
                                <div className="flex justify-between items-start gap-2">
                                   <div>
                                     <h4 className="font-bold text-lg text-gray-900 dark:text-gray-50 line-clamp-2 leading-tight">{book.title}</h4>
                                     <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">{book.author}</p>
                                   </div>
                                   <div className="px-2.5 py-1 bg-white dark:bg-gray-800 rounded-md border border-gray-100 dark:border-gray-700 shadow-sm text-xs font-semibold text-gray-600 dark:text-gray-300 tabular-nums">
                                     {book.year || 'N/A'}
                                   </div>
                                </div>
                                
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <Badge variant="outline" className={`border ${
                                    {
                                      'Fiction': 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 border-purple-100 dark:border-purple-800',
                                      'Non-Fiction': 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border-blue-100 dark:border-blue-800',
                                      'Science': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border-emerald-100 dark:border-emerald-800',
                                      'Technology': 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300 border-cyan-100 dark:border-cyan-800',
                                      'History': 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border-amber-100 dark:border-amber-800',
                                    }[book.category || ''] || 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-100 dark:border-gray-800'
                                  }`}>
                                    {book.category || 'General'}
                                  </Badge>
                                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 border-0">
                                    {book.available} of {book.quantity} Copies Available
                                  </Badge>
                                </div>
                                
                                {book.description && (
                                   <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 line-clamp-3 leading-relaxed">
                                      {book.description}
                                   </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Member Selection Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2 mt-2">
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${selectedBook ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>2</div>
                        <Label className={`text-sm font-semibold ${selectedBook ? 'text-black dark:text-white' : 'text-gray-400'}`}>Search Member</Label>
                      </div>
                      
                      {/* Search Input with Real-time Suggestions */}
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Type name or registration number..."
                            value={selectedMember ? (members.find(m => m.id === selectedMember)?.name + ' (' + members.find(m => m.id === selectedMember)?.memberId + ')') : memberSearchTerm}
                            onChange={(e) => {
                              setMemberSearchTerm(e.target.value);
                              if (selectedMember) setSelectedMember("");
                            }}
                            onFocus={() => {
                              if (selectedMember) {
                                setMemberSearchTerm("");
                                setSelectedMember("");
                              }
                            }}
                            className="pl-10 pr-10 h-11 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] focus:border-blue-500 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/50"
                          />
                          {(memberSearchTerm || selectedMember) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
                              onClick={() => {
                                setMemberSearchTerm("");
                                setSelectedMember("");
                              }}
                            >
                              <span className="text-gray-400 text-lg">×</span>
                            </Button>
                          )}
                        </div>
                        
                        {/* Real-time Suggestions Dropdown */}
                        {memberSearchTerm && !selectedMember && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-lg overflow-hidden">
                            {isSearchingMembers ? (
                              <div className="p-4 text-center text-sm text-muted-foreground">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                                  Searching...
                                </div>
                              </div>
                            ) : memberSearchResults.length === 0 ? (
                              <div className="p-4 text-center text-sm text-muted-foreground">
                                <p>No members found matching "{memberSearchTerm}"</p>
                              </div>
                            ) : (
                              <div className="max-h-64 overflow-y-auto">
                                {memberSearchResults.map((member) => {
                                  const limit = fineConfig.maxBooksPerUser || member.maxBorrowLimit;
                                  const canBorrow = member.borrowedBooks < limit;
                                  return (
                                    <button
                                      key={member.id}
                                      type="button"
                                      className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-800 last:border-0 transition-colors"
                                      onClick={() => {
                                        setSelectedMember(member.id);
                                        setMemberSearchTerm("");
                                        // Store the member in state for preview since it came from search
                                        setMembers(prev => {
                                          if (prev.find(m => m.id === member.id)) return prev;
                                          return [...prev, member];
                                        });
                                      }}
                                    >
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="font-semibold text-sm text-black dark:text-white">{member.name}</div>
                                          <div className="text-xs text-muted-foreground mt-0.5">
                                            <span className="uppercase">{member.role}</span> • {member.memberId}
                                          </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                          <Badge className={`text-xs border-0 ${canBorrow ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                                            {member.borrowedBooks}/{limit} books
                                          </Badge>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Member Preview Card */}
                      {selectedMember && (() => {
                        const member = members.find(m => m.id === selectedMember);
                        if (!member) return null;
                        const limit = fineConfig.maxBooksPerUser || member.maxBorrowLimit;
                        const progress = (member.borrowedBooks / limit) * 100;
                        const canBorrow = member.borrowedBooks < limit;
                        return (
                          <div className="mt-3 p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 border border-indigo-100 dark:border-indigo-900/50">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-bold text-sm text-black dark:text-white">{member.name}</h4>
                                <p className="text-xs text-muted-foreground mt-0.5 uppercase tracking-wide">{member.role} • {member.memberId}</p>
                              </div>
                              <Badge className={`text-xs border-0 ${canBorrow ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'}`}>
                                {canBorrow ? 'Can Borrow' : 'Limit Reached'}
                              </Badge>
                            </div>
                            <div className="mt-3 space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Books borrowed</span>
                                <span className="font-semibold text-black dark:text-white">{member.borrowedBooks} / {limit}</span>
                              </div>
                              <div className="h-2 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${progress >= 100 ? 'bg-red-500' : progress >= 66 ? 'bg-amber-500' : 'bg-blue-500'}`} 
                                  style={{ width: `${Math.min(100, progress)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Due Date Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2 mt-2">
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${selectedBook && selectedMember ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>3</div>
                        <Label className={`text-sm font-semibold ${selectedBook && selectedMember ? 'text-black dark:text-white' : 'text-gray-400'}`}>Set Due Date</Label>
                      </div>
                      
                      {/* Quick Date Presets */}
                      <div className="flex flex-wrap gap-2">
                        {[7, 14, 21, 30].map(days => (
                          <Button
                            key={days}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setDueDate(addDays(new Date(), days))}
                            className={`text-xs h-8 px-3 ${dueDate && differenceInCalendarDays(dueDate, new Date()) === days 
                              ? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
                              : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                          >
                            {days} days
                          </Button>
                        ))}
                      </div>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="w-full justify-start text-left font-medium bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-11 mt-2"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-blue-500" />
                            {dueDate ? (
                              <span className="flex items-center gap-2">
                                <span className="font-semibold">{format(dueDate, "EEEE, MMMM d, yyyy")}</span>
                                <Badge
                                  variant="outline"
                                  className="text-xs ml-auto border border-orange-400 dark:border-orange-500 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200"
                                >
                                  {differenceInCalendarDays(dueDate, new Date())} days
                                </Badge>
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Pick a custom date...</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]" align="start">
                          <Calendar
                            mode="single"
                            selected={dueDate}
                            onSelect={setDueDate}
                            initialFocus
                            disabled={{ before: new Date() }}
                            className="bg-[#FFFFFF] dark:bg-[#020817] text-black dark:text-white"
                            modifiersClassNames={{
                              today: "bg-accent/50 text-accent-foreground font-medium border border-primary/50"
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-[#F1F5F9] dark:border-[#1E293B] bg-gray-50 dark:bg-gray-900/50">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button 
                        onClick={handleBorrowBook} 
                        disabled={isBorrowing || !selectedBook || !selectedMember || !dueDate} 
                        className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white h-11 font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isBorrowing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Confirm & Issue Book
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setIsBorrowDialogOpen(false);
                          setSelectedBook("");
                          setBookSearchTerm("");
                          setSelectedMember("");
                          setMemberSearchTerm("");
                          setDueDate(undefined);
                        }}
                        className="sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#131E31] h-11"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog 
                open={isReturnDialogOpen} 
                onOpenChange={(open) => {
                  setIsReturnDialogOpen(open);
                  if (!open) {
                    setReturnSelectedMemberId("");
                    setReturnMemberSearchTerm("");
                    setMemberBorrowedBooks([]);
                    setReturnMemberSearchResults([]);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="m-2 flex-shrink-0 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                  >
                    <RotateCcw className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span className="hidden sm:inline">Return Book</span>
                    <span className="sm:hidden">Return</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] p-0 gap-0">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-[#F1F5F9] dark:border-[#1E293B] bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-950/30 dark:to-emerald-950/30">
                    <DialogHeader>
                      <DialogTitle className="text-xl font-bold text-black dark:text-white flex items-center gap-2">
                        <div className="p-2 bg-teal-100 dark:bg-teal-900/50 rounded-lg">
                          <RotateCcw className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                        </div>
                        Return Book
                      </DialogTitle>
                      <DialogDescription className="text-sm text-muted-foreground mt-1">
                        Search for a member and select a book to return.
                      </DialogDescription>
                    </DialogHeader>
                  </div>

                  <div className="px-6 py-6 space-y-6">
                    {/* Member Search Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${returnSelectedMemberId ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>1</div>
                        <Label className={`text-sm font-semibold ${returnSelectedMemberId ? 'text-black dark:text-white' : 'text-gray-400'}`}>Search Member</Label>
                      </div>
                      
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Type name or registration number..."
                            value={returnMemberSearchTerm}
                            onChange={(e) => {
                              setReturnMemberSearchTerm(e.target.value);
                              if (returnSelectedMemberId) {
                                setReturnSelectedMemberId("");
                                setMemberBorrowedBooks([]);
                              }
                            }}
                            className="pl-10 pr-10 h-11 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] focus:border-teal-500 dark:focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/50 mb-2"
                          />
                          {(returnMemberSearchTerm || returnSelectedMemberId) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
                              onClick={() => {
                                setReturnMemberSearchTerm("");
                                setReturnSelectedMemberId("");
                                setMemberBorrowedBooks([]);
                              }}
                            >
                              <span className="text-gray-400 text-lg">×</span>
                            </Button>
                          )}
                        </div>

                        {/* Suggestions Dropdown */}
                        {returnMemberSearchTerm && !returnSelectedMemberId && (
                          <div className="w-full mt-1 bg-white dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-lg overflow-hidden">
                            {isSearchingReturnMembers ? (
                              <div className="p-4 text-center text-sm text-muted-foreground">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-teal-500 border-t-transparent" />
                                  Searching...
                                </div>
                              </div>
                            ) : returnMemberSearchResults.length === 0 ? (
                              <div className="p-4 text-center text-sm text-muted-foreground">
                                <p>No members found matching "{returnMemberSearchTerm}"</p>
                              </div>
                            ) : (
                              <div className="max-h-64 overflow-y-auto">
                                {returnMemberSearchResults.map((member) => (
                                  <button
                                    key={member.id}
                                    type="button"
                                    className="w-full px-4 py-3 text-left hover:bg-teal-50 dark:hover:bg-teal-900/20 border-b border-gray-100 dark:border-gray-800 last:border-0 transition-colors"
                                    onClick={() => {
                                      setReturnSelectedMemberId(member.id);
                                      setReturnMemberSearchTerm(member.name);
                                      setReturnMemberSearchResults([member]); // Keep the selected member in the list
                                      fetchMemberBorrowedBooks(member);
                                    }}
                                  >
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-semibold text-sm text-black dark:text-white">{member.name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                          <span className="uppercase">{member.role}</span> • {member.memberId}
                                        </div>
                                      </div>
                                      <Badge className="text-xs border-0 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                        {member.borrowedBooks} borrowed
                                      </Badge>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Selected Member Preview */}
                      {returnSelectedMemberId && (() => {
                        const member = returnMemberSearchResults.find(m => m.id === returnSelectedMemberId) || { name: returnMemberSearchTerm, role: 'Unknown', memberId: 'Unknown', borrowedBooks: 0 };
                        return (
                          <div className="mt-3 p-4 rounded-xl bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-950/20 dark:to-emerald-950/20 border border-teal-100 dark:border-teal-900/50">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-bold text-sm text-black dark:text-white">{member.name}</h4>
                                <p className="text-xs text-muted-foreground mt-0.5 uppercase tracking-wide">{member.role} • {member.memberId}</p>
                              </div>
                              <Badge className="bg-white dark:bg-black/20 text-teal-700 dark:text-teal-300 border-0">
                                {member.borrowedBooks} Books Currently Borrowed
                              </Badge>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Borrowed Books Catalogue */}
                    {returnSelectedMemberId && (
                      <div className="space-y-3 pt-2">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${memberBorrowedBooks.length > 0 ? 'bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>2</div>
                          <Label className={`text-sm font-semibold ${memberBorrowedBooks.length > 0 ? 'text-black dark:text-white' : 'text-gray-400'}`}>Select Book to Return</Label>
                        </div>

                        {isLoadingMemberBooks ? (
                          <div className="py-8 text-center text-sm text-muted-foreground">
                            <div className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-teal-500 border-t-transparent" />
                              Loading books...
                            </div>
                          </div>
                        ) : memberBorrowedBooks.length === 0 ? (
                          <div className="py-8 text-center p-4 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50/50 dark:bg-gray-900/50">
                            <BookOpen className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                            <p className="text-sm font-medium text-muted-foreground">This member has no books to return.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-3 max-h-[300px] overflow-y-auto pr-1">
                            {memberBorrowedBooks.map((record) => {
                              const displayReg = getDisplayReg(record);
                              const isOverdue = getEffectiveStatus(record) === "overdue";
                              
                              return (
                                <div 
                                  key={record.id}
                                  className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 hover:border-teal-200 dark:hover:border-teal-800 transition-colors shadow-sm"
                                >
                                  {/* Icon / Date Info */}
                                  <div className="flex-shrink-0 flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2">
                                     <div className={`p-2 rounded-md ${isOverdue ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'}`}>
                                        <BookOpen className="h-5 w-5" />
                                     </div>
                                     <div className="text-xs font-medium text-center sm:text-left">
                                        <div className="text-muted-foreground">Due</div>
                                        <div className={isOverdue ? "text-red-500 font-bold" : "text-gray-700 dark:text-gray-300"}>{record.dueDate}</div>
                                     </div>
                                  </div>

                                  {/* Book Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start gap-2">
                                      <h4 className="font-semibold text-sm text-black dark:text-white line-clamp-1">{record.bookTitle}</h4>
                                      {isOverdue && <Badge variant="destructive" className="text-[10px] h-5">Overdue</Badge>}
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                                      <span>Borrowed: {record.borrowDate}</span>
                                      <span>Copy ID: {displayReg}</span>
                                    </div>
                                    
                                    <div className="mt-3 flex justify-end gap-2">
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => handleRenewBook(record.id)}
                                        className="h-8 text-xs bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]"
                                      >
                                        Renew
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        onClick={() => handleReturnBook(record.id)}
                                        className="h-8 text-xs bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-700 text-white"
                                      >
                                        Return Book
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="flex-1 min-h-0">
            <Card className="h-full flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="p-0 flex-1 min-h-0">
                <div className="h-full overflow-auto no-scrollbar">
                  <div className="mobile-table-container overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                          <TableHead className="min-w-[150px] text-black dark:text-white">Book</TableHead>
                          <TableHead className="hidden sm:table-cell w-[150px] text-black dark:text-white">Member</TableHead>
                          <TableHead className="hidden md:table-cell w-[120px] text-black dark:text-white">Due Date</TableHead>
                          <TableHead className="w-[100px] text-black dark:text-white">Status</TableHead>
                          <TableHead className="hidden sm:table-cell w-[80px] text-black dark:text-white">Fine</TableHead>
                          <TableHead className="w-[120px] text-right text-black dark:text-white">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRecords.map((record) => {
                          const displayReg = getDisplayReg(record);
                          return (
                            <TableRow key={record.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                              <TableCell className="font-medium pr-2 text-black dark:text-white py-2 book-cell">
                                <div className="min-w-0">
                                  <div className="truncate">{record.bookTitle}</div>
                                  <div className="text-xs text-muted-foreground sm:hidden truncate">{record.memberName}</div>
                                </div>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell text-black dark:text-white py-2">
                                <div className="min-w-0">
                                  <div className="truncate text-xs font-medium">{record.memberName}</div>
                                  <div className="truncate text-xs text-muted-foreground">{displayReg}</div>
                                </div>
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-black dark:text-white py-2">
                                <div className="text-xs">
                                  <div>{record.dueDate}</div>
                                  {getEffectiveStatus(record) === "overdue" && (
                                    <div className="text-[#EF4444] dark:text-[#F87171]">Overdue</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="py-2">
                                {getStatusBadge(record)}
                              </TableCell>
                              <TableCell className="hidden sm:table-cell text-center text-xs text-black dark:text-white py-2">
                                {(() => {
                                  const fine = record.status === "borrowed" 
                                      ? calculateDynamicFine(record) 
                                      : record.fineAmount;
                                  return `${fineConfig.currency} ${fine.toFixed(0)}`;
                                })()}
                              </TableCell>
                              <TableCell className="text-right py-2">
                                <div className="flex gap-1 justify-end">
                                  {record.status === "borrowed" && (
                                    <>
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => handleReturnBook(record.id)}
                                        className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-9 px-2 text-xs flex items-center gap-1"
                                      >
                                        <RotateCcw className="h-3 w-3" />
                                        Return
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => handleRenewBook(record.id)}
                                        className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-9 px-2 text-xs flex items-center gap-1"
                                      >
                                        <RotateCcw className="h-3 w-3" />
                                        Renew
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
              <div className="flex flex-col items-center gap-4 pt-4 pb-6 border-t border-[#F1F5F9] dark:border-[#1E293B]">
                <div className="text-sm text-muted-foreground text-center">
                  Page {currentPage} {totalRecords > 0 ? `of ~${estimatedTotalPages}` : ''} ({filteredRecords.length} shown)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-blue-600 text-white">
                    {currentPage}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={records.length < itemsPerPage}
                    className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {filteredRecords.length === 0 && (
            <Card className="flex-shrink-0 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="text-center py-12">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">No records found</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {searchTerm || filterStatus !== "all" ? "Try adjusting your search or filters" : "Start by borrowing your first book"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-black dark:text-white">
              {confirmAction === 'return' ? 'Process Return' : 'Confirm Renew'}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
               {confirmAction === 'return' ? "Check condition and fines below." : "Extend due date by 14 days?"}
               {confirmAction === 'return' && confirmRecord && (() => {
                  const today = new Date();
                  const due = new Date(confirmRecord.dueDate);
                  const diff = differenceInCalendarDays(today, due);
                  if (diff > 0) {
                     return <div className="mt-2 font-medium text-red-500">Book is overdue by {diff} day{diff !== 1 ? 's' : ''}</div>;
                  }
                  return <div className="mt-2 font-medium text-green-500">Book is being returned on time</div>;
               })()}
            </DialogDescription>
          </DialogHeader>

          {confirmAction === 'return' ? (
             <div className="space-y-4 py-2">
                 <div className="flex flex-col gap-2 mb-2">
                    <Label>Condition</Label>
                    <Select value={returnState.condition} onValueChange={(val: any) => handleConditionChange(val)}>
                      <SelectTrigger className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] text-black dark:text-white">
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                        <SelectItem value="good" className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Good (Normal Return)</SelectItem>
                        <SelectItem value="damaged" className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Damaged (Charge Fine)</SelectItem>
                        <SelectItem value="lost" className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Lost (Charge Replacement)</SelectItem>
                      </SelectContent>
                    </Select>
                 </div>

                 <div className="flex flex-col gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-800">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Calculated Fine ({fineConfig.currency})</span>
                        <span className="text-base font-bold">{returnState.calculatedFine}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2">
                        <input 
                           type="checkbox" 
                           id="override" 
                           className="h-4 w-4"
                           checked={returnState.isManualOverride} 
                           onChange={(e) => setReturnState({...returnState, isManualOverride: e.target.checked})}
                        />
                        <label htmlFor="override" className="text-sm cursor-pointer select-none">Override Amount</label>
                    </div>

                    {returnState.isManualOverride && (
                        <div className="mt-2">
                           <Input 
                              type="number" 
                              value={returnState.manualFine} 
                              onChange={(e) => setReturnState({...returnState, manualFine: parseFloat(e.target.value) || 0})}
                              className="bg-white dark:bg-[#020817]"
                           />
                        </div>
                    )}
                    
                    <div className="flex items-center gap-2 mt-2">
                        <input 
                           type="checkbox" 
                           id="waive" 
                           className="h-4 w-4"
                           checked={returnState.waived} 
                           onChange={(e) => setReturnState({...returnState, waived: e.target.checked})}
                        />
                        <label htmlFor="waive" className="text-sm cursor-pointer select-none">Waive All Fines</label>
                    </div>
                 </div>
                 
                 <div className="flex justify-between items-center pt-2 border-t mt-1">
                    <span className="font-bold">Total Due:</span>
                    <span className={`font-bold text-lg ${currentTotalFine > 0 ? 'text-red-500' : 'text-green-500'}`}>
                       {fineConfig.currency} {currentTotalFine}
                    </span>
                 </div>
                 
                 <div className="flex flex-col gap-2"> 
                    <Label htmlFor="remarks">Remarks (optional)</Label>
                    <Input 
                        id="remarks"
                        value={returnState.remarks} 
                        onChange={(e) => setReturnState({...returnState, remarks: e.target.value})}
                        placeholder="e.g. Broken cover..."
                        className="bg-white dark:bg-[#020817]"
                    />
                 </div>
             </div>
          ) : (
            <div className="py-4">
               <p>This will extend the due date to <strong>{format(addDays(new Date(), 14), "PPP")}</strong>.</p>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsConfirmOpen(false)}
              className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm} 
              className="bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] ml-2"
            >
              Confirm {confirmAction === 'return' ? 'Return' : 'Renew'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export { BorrowReturnSystem };